import { Link, NavLink } from "react-router-dom";
import { Menu, X, User, ShoppingCart, LayoutDashboard, Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import TopBar from "@/components/TopBar";
import HALogo from "@/components/HALogo";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";

const STAFF_ROLES = ["admin", "owner", "manager", "employee"];

const links = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/videos", label: "Videos" },
  { to: "/learn", label: "Learn" },
  { to: "/lab-reports", label: "Lab Reports" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const { user } = useAuth();
  const { count } = useCart();
  const isStaff = STAFF_ROLES.includes(user?.role);

  useEffect(() => {
    api.get("/products", { params: { is_new: true } })
      .then((r) => setHasNew((r.data || []).length > 0))
      .catch(() => {});
  }, []);

  return (
    <header className="sticky top-0 z-40 bg-navy/90 backdrop-blur-xl border-b border-copper/20">
      <div className="max-w-[1600px] mx-auto px-5 lg:px-8 h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 shrink-0">
          <HALogo className="h-11 lg:h-12 w-auto" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-copper border-l border-copper/30 pl-3 hidden xl:inline">Texarkana TX</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `relative text-[13px] tracking-[0.12em] uppercase whitespace-nowrap transition-colors ${
                  isActive ? "text-copper" : "text-sand/75 hover:text-copper"
                }`
              }
              end={l.to === "/"}
            >
              <span className="inline-flex items-center gap-1.5">
                {l.label}
                {l.label === "Shop" && hasNew && (
                  <span data-testid="nav-shop-new-pill" className="bg-copper text-white px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] leading-none">New</span>
                )}
              </span>
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4 xl:gap-5 shrink-0">
          <a
            href="https://TEXTHC.COM"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-flower-website"
            className="flex flex-col items-center leading-tight text-center border border-copper/40 rounded-md px-4 py-1.5 hover:border-copper hover:bg-copper/10 transition-colors group whitespace-nowrap"
          >
            <span className="text-[13px] tracking-[0.12em] uppercase text-copper group-hover:text-[#E5C158]">Flower Website</span>
            <span className="text-[9px] tracking-[0.1em] uppercase text-sand/60 group-hover:text-sand/80">Order Flower Here!</span>
          </a>
          <span className="h-6 w-px bg-copper/20" />
          {isStaff && (
            <Link to="/admin/products" data-testid="header-edit-products" className="inline-flex items-center gap-1.5 text-[13px] uppercase tracking-[0.12em] text-copper hover:text-[#E5C158] whitespace-nowrap">
              <Pencil size={14}/> Edit
            </Link>
          )}
          {isStaff && (
            <Link to="/admin" data-testid="header-console" className="inline-flex items-center gap-1.5 text-[13px] uppercase tracking-[0.12em] text-copper hover:text-[#E5C158] whitespace-nowrap">
              <LayoutDashboard size={14}/> Console
            </Link>
          )}
          {user ? (
            <Link to="/account" data-testid="header-account" className="inline-flex items-center gap-1.5 text-[13px] uppercase tracking-[0.12em] text-sand/85 hover:text-copper whitespace-nowrap">
              <User size={14}/> {user.name?.split(" ")[0] || "Account"}
            </Link>
          ) : (
            <Link to="/login" data-testid="header-login" className="text-[13px] uppercase tracking-[0.12em] text-sand/85 hover:text-copper whitespace-nowrap">Sign In</Link>
          )}
          <Link to="/cart" data-testid="header-cart" className="relative inline-flex items-center text-sand/85 hover:text-copper" aria-label="Cart">
            <ShoppingCart size={20} />
            {count > 0 && (
              <span data-testid="header-cart-count" className="absolute -top-2 -right-2.5 bg-copper text-navy text-[10px] font-bold leading-none rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{count}</span>
            )}
          </Link>
          <Link
            to="/shop"
            data-testid="header-shop-btn"
            className="bg-copper text-navy px-5 py-2.5 text-[13px] tracking-[0.12em] uppercase font-semibold hover:bg-[#E5C158] transition-colors whitespace-nowrap"
          >
            Shop
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-4">
          <Link to="/cart" data-testid="mobile-header-cart" className="relative inline-flex items-center text-sand" aria-label="Cart">
            <ShoppingCart size={22} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2.5 bg-copper text-navy text-[10px] font-bold leading-none rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{count}</span>
            )}
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-copper/20 bg-navy/97 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-base text-sand/85 hover:text-copper inline-flex items-center gap-2"
                end={l.to === "/"}
              >
                {l.label}
                {l.label === "Shop" && hasNew && (
                  <span data-testid="mobile-nav-shop-new-pill" className="bg-copper text-navy px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] leading-none">New</span>
                )}
              </NavLink>
            ))}
            <a
              href="https://TEXTHC.COM"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-flower-website"
              className="flex flex-col leading-tight border border-copper/40 rounded-md px-4 py-3 hover:border-copper hover:bg-copper/10 transition-colors"
            >
              <span className="text-base uppercase tracking-wide text-copper">Flower Website</span>
              <span className="text-xs uppercase tracking-wide text-sand/60">Order Flower Here!</span>
            </a>
            <Link
              to="/shop"
              onClick={() => setOpen(false)}
              className="bg-copper text-navy font-semibold px-6 py-3 text-sm tracking-wide uppercase text-center"
            >
              Shop Products
            </Link>
            {isStaff && (
              <Link
                to="/admin/products"
                onClick={() => setOpen(false)}
                data-testid="mobile-header-edit-products"
                className="border border-copper text-copper font-semibold px-6 py-3 text-sm tracking-wide uppercase text-center inline-flex items-center justify-center gap-2"
              >
                <Pencil size={16}/> Edit Products
              </Link>
            )}
            {isStaff && (
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                data-testid="mobile-header-console"
                className="border border-copper text-copper font-semibold px-6 py-3 text-sm tracking-wide uppercase text-center inline-flex items-center justify-center gap-2"
              >
                <LayoutDashboard size={16}/> Employee Console
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
